'use client';

import { faArrowLeft, faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { LaboratoryExamItem } from './laboratory-exam-item.component';
import { LaboratoryExamsFooter } from './laboratory-exams-footer.component';
import { UseLaboratoryExamSelectionReturn } from './_hooks/use-laboratory-exam-selection.hook';
import { useLaboratoryExams } from './_hooks/use-laboratory-exams.hook';

import { LaboratoryErrorFallback } from '@/app/(main)/(pages)/(institucional)/(index)/laboratorios/_components/laboratory-error-fallback.component';
import { Button } from '@/components/ui/button/button.component';
import { Icon } from '@/components/ui/icon.component';
import { Input } from '@/components/ui/input/input.component';
import { Sheet, SheetClose, SheetContent } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';

const SKELETON_ROWS = 6;

interface LaboratoryExamsModalProps {
  facilityId: string;
  laboratoryName: string;
  open: boolean;
  selection: UseLaboratoryExamSelectionReturn;
  onOpenChange(open: boolean): void;
  onSeeSchedules(): void;
}

export function LaboratoryExamsModal({
  facilityId,
  laboratoryName,
  open,
  selection,
  onOpenChange,
  onSeeSchedules,
}: LaboratoryExamsModalProps) {
  const {
    exams,
    term,
    setTerm,
    isLoading,
    isLoadingMore,
    hasMore,
    hasError,
    hasLoadMoreError,
    loadMore,
    retry,
    retryLoadMore,
  } = useLaboratoryExams(facilityId, { enabled: open });

  const showsEmptyState = !isLoading && !hasError && exams.length === 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="center-v2"
        className="min-[768px]:h-fit min-[768px]:w-[90vw] min-[768px]:rounded-2xl min-[768px]:max-h-[80vh] md:max-h-[80vh] min-[768px]:max-w-[480px] md:max-w-[480px] p-0 flex flex-col"
        hasHeader
        title={`Exames em ${laboratoryName}`}
        data-testid={DataTestIdEnum.BRANCH_LABORATORY_EXAMS_MODAL}
      >
        <div className="flex flex-col gap-4 px-6 pt-4">
          <div className="flex items-center gap-3">
            <SheetClose
              asChild
              className="text-gray-500 hover:text-gray-900"
              data-testid={DataTestIdEnum.BRANCH_LABORATORY_EXAMS_MODAL_CLOSE}
            >
              <button type="button" aria-label="Fechar" className="size-10 shrink-0 flex items-center justify-center">
                <Icon icon={faArrowLeft} size="lg" />
              </button>
            </SheetClose>

            <Input
              data-testid={DataTestIdEnum.BRANCH_LABORATORY_EXAMS_MODAL_SEARCH}
              aria-label="Busque pelo exame"
              placeholder="Busque pelo exame"
              startIcon={faMagnifyingGlass}
              value={term}
              onChange={(event) => setTerm(event.target.value)}
            />
          </div>

          <Typography variant={TypographyVariant.BODY_TEXT} as="p" className="font-bold text-gray-700">
            {laboratoryName}
          </Typography>
        </div>

        <div className="flex-1 overflow-y-auto">
          {isLoading && (
            <div className="flex flex-col gap-4 px-6 py-4" role="status" aria-label="Carregando exames">
              {Array.from({ length: SKELETON_ROWS }).map((_, index) => (
                <Skeleton key={index} className="h-12 w-full" />
              ))}
            </div>
          )}

          {!isLoading && hasError && (
            <LaboratoryErrorFallback
              message="Não foi possível carregar os exames."
              laboratoryId={facilityId}
              dataTestId={DataTestIdEnum.BRANCH_LABORATORY_EXAMS_MODAL_ERROR}
              onRetry={retry}
            />
          )}

          {showsEmptyState && (
            <Typography
              variant={TypographyVariant.BODY_TEXT}
              as="p"
              data-testid={DataTestIdEnum.BRANCH_LABORATORY_EXAMS_MODAL_EMPTY}
              className="px-6 py-10 text-center text-gray-500"
            >
              Nenhum exame encontrado.
            </Typography>
          )}

          {exams.map((exam) => (
            <LaboratoryExamItem
              key={exam.id}
              exam={exam}
              checked={selection.isSelected(exam.id)}
              disabled={selection.isFull && !selection.isSelected(exam.id)}
              onToggle={selection.toggle}
            />
          ))}

          {hasLoadMoreError && (
            <div
              role="alert"
              data-testid={DataTestIdEnum.BRANCH_LABORATORY_EXAMS_MODAL_LOAD_MORE_ERROR}
              className="flex flex-col items-center gap-2 px-6 pt-4"
            >
              <Typography variant={TypographyVariant.BODY_TEXT} className="text-center text-gray-500">
                Não foi possível carregar mais exames.
              </Typography>
              <Button variant="secondary" isLoading={isLoadingMore} onClick={retryLoadMore}>
                Tentar novamente
              </Button>
            </div>
          )}

          {hasMore && !hasLoadMoreError && (
            <div className="px-6 py-4">
              <Button
                variant="secondary"
                className="w-full"
                isLoading={isLoadingMore}
                disabled={isLoadingMore}
                data-testid={DataTestIdEnum.BRANCH_LABORATORY_EXAMS_MODAL_LOAD_MORE}
                onClick={loadMore}
              >
                Mostrar mais exames
              </Button>
            </div>
          )}
        </div>

        <LaboratoryExamsFooter
          count={selection.count}
          total={selection.total}
          isFull={selection.isFull}
          onSeeSchedules={onSeeSchedules}
        />
      </SheetContent>
    </Sheet>
  );
}
